// @flow
import React from "react";
import { toastr as reduxToastr } from "react-redux-toastr";
import Toastr from "components/Toastr";

// function flattens django errors like {email: ['content'], items: [{name: ['required']}]} to flat object: {email: 'content', items: [{name: 'required'}]}
export function flattenErrors(error: Object): Object {
  if (!error) return {};

  const errors = {};

  if (Array.isArray(error)) {
    errors.non_field_errors = error;
    errors.non_field_error = error[0];
    return errors;
  }

  Object.keys(error).forEach(key => {
    if (typeof error[key] === "string") {
      errors[key] = error[key];
    } else if (error[key] instanceof Array) {
      if (error[key][0] instanceof Object) {
        errors[key] = error[key].map(err => flattenErrors(err));
      } else {
        errors[key] = error[key][0];
      }
    }
  });

  Object.defineProperty(errors, "$any", {
    value: errors[Object.keys(errors)[0]],
    enumerable: false,
  });

  return errors;
}

// function parses query string to js object
export function parseQuery(link: string = "") {
  if (!link || link.length === 0 || link.indexOf("?") === -1) {
    return {};
  }
  const qstr = link.split("?")[1];
  const query = {};
  const a = qstr.split("&");
  for (var i = 0; i < a.length; i++) {
    var b = a[i].split("=");
    query[decodeURIComponent(b[0])] = decodeURIComponent(b[1] || "");
  }
  return query;
}

export const deepQueryDiff = (obj1, obj2, exclude = "panel_id") => {
  if (obj1 === obj2) return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  const bigger = keys1.length > keys2.length ? keys1 : keys2;
  return bigger.find(key => key !== exclude && obj1[key] !== obj2[key]) !== undefined;
};

// function serialize js object to url query string.
// setting allowEmpty: false will not return empty param.
export function stringifyQuery(query: Object, allowEmpty: boolean = false, forceFirstSign) {
  let queryString = "";
  Object.keys(query).forEach((key, index) => {
    let param = "";
    if (queryString.length === 0) {
      param += "?";
    } else {
      param += "&";
    }
    param += key + "=";
    param += query[key];
    if (
      (query[key] !== undefined && query[key] !== null && query[key] !== "") ||
      allowEmpty === true
    ) {
      queryString += param;
    }
  });

  if (queryString.length > 0 && forceFirstSign) {
    queryString = forceFirstSign + queryString.substring(1);
  }

  return queryString;
}

export function mergeQueryStrings(queryStrings) {
  let query = {};
  queryStrings.forEach(string => {
    query = { ...query, ...parseQuery(string) };
  });
  return stringifyQuery(query, false);
}

export function getDate(data, { humanize, hour = false, separator = "/" }) {
  if (!data) {
    return "---";
  }
  let date = new Date(data);
  let dd = date.getDate();
  let mm = date.getMonth() + 1; //January is 0!
  let yyyy = date.getFullYear();
  let hh = date.getHours();
  let min = date.getMinutes();

  if (dd < 10) {
    dd = "0" + dd;
  }

  if (mm < 10) {
    mm = "0" + mm;
  }

  if (min < 10) {
    min = "0" + min;
  }

  if (humanize) {
    date = `${dd} ${humanizeDate(mm)} ${yyyy}`;
    if (hour) {
      date += `, ${hh}:${min}`;
    }
  } else {
    date = dd + separator + mm + separator + yyyy;
    if (hour) {
      date += ", " + hh + ":" + min;
    }
  }
  return date;
}

export function getHour(dateString: string): string {
  const date = new Date(dateString);
  let hour = date.getHours();
  let minutes = date.getMinutes();
  if (hour < 10) hour = "0" + hour;
  if (minutes < 10) minutes = "0" + minutes;

  return `${hour}:${minutes}`;
}

export function humanizeDate(day): string {
  day = day.toString();

  if (day.length === 1) {
    day = "0" + day;
  }

  switch (day) {
    case "01":
      return "stycznia";
    case "02":
      return "lutego";
    case "03":
      return "marca";
    case "04":
      return "kwietnia";
    case "05":
      return "maja";
    case "06":
      return "czerwca";
    case "07":
      return "lipca";
    case "08":
      return "sierpnia";
    case "09":
      return "września";
    case "10":
      return "października";
    case "11":
      return "listopada";
    case "12":
      return "grudnia";
    default:
      return undefined;
  }
}

// return number of days between two dates
export function dateDiff(date1, date2): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());
  return Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
}

type Pagination = {
  next: ?number,
  prev: ?number,
  count: ?number,
  page: ?number,
  limit: ?number,
};

// prepare django pagination object
export function getPagination({
  next,
  previous,
  count = null,
  limit = null,
  page_size,
}): Pagination {
  function getPageSize() {
    return count && limit ? Math.ceil(count / limit / 10) * 10 : null;
  }
  const nextPage = parseInt(parseQuery(next).page, 10) || null;
  const prevPage = previous ? parseInt(parseQuery(previous).page, 10) || 1 : null;
  const page = (function() {
    if (nextPage) return nextPage - 1;
    if (prevPage) return prevPage + 1;
    return 1;
  })();
  const pageSize = page_size || getPageSize();
  let indexes = [];

  if (pageSize) {
    let i = 0;
    for (i; i < pageSize; i += 1) {
      indexes.push(pageSize * (page - 1) + i + 1);
    }
  }

  return {
    next: nextPage,
    prev: prevPage,
    count,
    page,
    limit,
    pageSize,
    indexes,
  };
}

// take safely nested attribute; safe(obj, 'prop1.prop2.prop3');
// if fail - return placeholder (default: undefined);
export function safe(obj, attr, placeholder): any {
  const attributes = attr.split(".");
  let newObj = { ...obj };
  attributes.forEach(attribute => {
    if (newObj && newObj[attribute]) {
      newObj = newObj[attribute];
    } else {
      newObj = placeholder;
      return newObj;
    }
  });
  return newObj;
}

export function mergeDedupe(arr): Array<Object> {
  if (!arr) return [];
  return [...new Set([].concat(...arr))];
}

// deduplicate array of objects by object attribute (e.g. id)
export function mergeDedupeByAttr(arr: Array<Object>, attr: string = "id"): Array<Object> {
  const res = arr.reduce(
    (acc, current, index) => {
      if (acc.ids.indexOf(current[attr]) === -1) {
        acc.ids.push(current[attr]);
        acc.indexes.push(index);
      }
      return acc;
    },
    { ids: [], indexes: [] }
  );
  return res.indexes.map(val => arr[val]);
}

export function sortByIdDesc(arr) {
  return [...arr].sort((a, b) => {
    if (a.id < b.id) return 1;
    if (a.id > b.id) return -1;
    return 0;
  });
}

export function sortAsc(arr) {
  return [...arr].sort((a, b) => a - b);
}

export function sortDesc(arr) {
  return [...arr].sort((a, b) => b - a);
}

export function dateValidate(date: string | Date): boolean {
  // check if date is valid (parses date to milisecond timestamp and check if isNaN).
  return isNaN(Date.parse(date)) === false;
}

export const isFirefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1;

export const parseDateToISO = (date: Date): string => {
  if (!dateValidate(date)) console.error("error in date ISO parsing");
  // parsing date to ISO format with timezone offset
  const tzo = -date.getTimezoneOffset();
  const dif = tzo >= 0 ? "+" : "-";
  const pad = function(num) {
    const norm = Math.floor(Math.abs(num));
    return (norm < 10 ? "0" : "") + norm;
  };

  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    ":" +
    pad(date.getSeconds()) +
    dif +
    pad(tzo / 60) +
    ":" +
    pad(tzo % 60)
  );
};

// export const getMonthSpan = (month: number) => {
//   // return full span of a given month (first day and last day)
//   const date = new Date();

//   const first = new Date(date.getFullYear(), month, 1);
//   const last = new Date(date.getFullYear(), month + 1, 0);
//   return { first, last };
// }

export const encode = (str: ?string): string => {
  if (btoa !== undefined && typeof str === "string") {
    return btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function(match, p1) {
        return String.fromCharCode(parseInt(p1, 16));
      })
    );
  }
  return "";
};

export const decode = (str: ?string): string => {
  if (atob !== undefined && typeof str === "string") {
    return decodeURIComponent(
      Array.prototype.map
        .call(atob(str), function(c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
  }
  return "";
};

export function getRandomArbitrary(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

export function getRandomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function normalize(arr, options = {}) {
  const { key = "id", mergeProps } = options;

  if (!arr || !arr.length) return { ids: [], elements: {} };
  return arr.reduce(
    (acc, curr) => {
      acc.elements[curr[key]] = curr;
      if (acc.ids.indexOf(curr[key]) === -1) {
        acc.ids.push(curr[key]);
        if (mergeProps) {
          Object.assign(acc, mergeProps(curr));
        }
      } else {
        console.error("duplicated keys!", arr);
      }
      return acc;
    },
    { ids: [], elements: {} }
  );
}

export function denormalize(obj: {}, options = {}): [] {
  const { mergeProps } = options;
  if (mergeProps)
    return obj.ids.map(id => Object.assign({}, obj.elements[id], mergeProps(obj.elements[id])));
  return obj.ids.map(id => obj.elements[id]);
}

export function mergeNormalized(...elems: any[]): {} {
  const result = { elements: {}, ids: [] };
  elems.forEach(el => {
    result.elements = {
      ...result.elements,
      ...el.elements,
    };
    result.ids = mergeDedupe([...result.ids, ...el.ids]);
  });
  return result;
}

export function addNormalized(acc, newEl) {
  return {
    elements: {
      ...acc.elements,
      [newEl.id]: newEl,
    },
    ids: mergeDedupe([...acc.ids, newEl.id]),
  };
}

export function removeNormalized(acc, id) {
  const result = {
    elements: {
      ...acc.elements,
    },
    ids: acc.ids.filter(el => el !== id),
  };
  delete result.elements[id];
  return result;
}

export const normalizer = {
  merge: mergeNormalized,
  add: addNormalized,
  remove: removeNormalized,
  normalize,
  denormalize,
};

export function createReducer(initialState, handlers) {
  return function reducer(state = initialState, action) {
    handlers.RESET_STORE = () => initialState;
    if (handlers.hasOwnProperty(action.type)) {
      return handlers[action.type].call(this, state, action);
    } else {
      return state;
    }
  };
}

export function switcher(input: string | number, handlers: {}): string {
  if (handlers.hasOwnProperty(input)) {
    return handlers[input];
  }
  return "---";
}

export const makeCancelable = (promise): {} => {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      val => (hasCanceled_ ? reject({ isCanceled: true }) : resolve(val)),
      error => (hasCanceled_ ? reject({ isCanceled: true }) : reject(error))
    );
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true;
    },
  };
};

export function to(promise) {
  return promise.then(
    data => {
      return data;
    },
    function(err) {
      return err;
    }
  );
}

/**
 * catches response and returns it as a array of [payload, error, canceled flag, status]
 * use for avoid try - catch blocks.
 */
export function catchRes(promise) {
  return promise.then(
    data => {
      return [data.payload, null, data.isCanceled, data.status];
    },
    function(err) {
      return [null, err.error, err.isCanceled, err.status];
    }
  );
}

export const getInitials = (fn: string | {}, ln: ?string): string => {
  if (typeof fn === "object")
    return (fn.first_name.substring(0, 1) + fn.last_name.substring(0, 1)).toUpperCase();
  return (fn.substring(0, 1) + ln.substring(0, 1)).toUpperCase();
};

export function isInt(n) {
  return parseInt(n, 10) === n;
}

export const pluralChecker = (number, obj) => {
  if (isInt(number) === false) return obj.plural;
  if (number === 1) return obj.singular;
  if ([12, 13, 14].includes(number)) return obj.other;
  if (["2", "3", "4"].includes(number.toString().slice(-1))) return obj.plural;
  return obj.other || obj.plural;
};

export function generateColors(amount): [] {
  let hue = 0;
  let colors = [];
  for (let i = 0; i < amount; i++) {
    colors.push("hsla(" + hue + ", 75%, 50%, 0.5)");
    hue += 222.5;
  }
  return colors;
}

export function moveCaretAtEnd(e) {
  var temp_value = e.target.value;
  e.target.value = "";
  e.target.value = temp_value;
}

export function requiredParam(param) {
  const requiredParamError = new Error(`Required parameter "${param}" is missing.`);

  // preserve original stack trace
  if (typeof Error.captureStackTrace === `function`) {
    Error.captureStackTrace(requiredParamError, requiredParam);
  }

  throw requiredParamError;
}

export function requiredType(input: any, typeString: "array" | "object"): boolean {
  const valid = switcher(typeString, {
    array: () => Array.isArray(input),
    object: () => !Array.isArray(input) && typeof input === "object",
  })();
  if (!valid) {
    throw new Error(`Parameter / variable type is invalid. required "${typeString}"`);
  } else {
    return true;
  }
}

export const toastr = {
  success(text: string) {
    reduxToastr.success("", " ", {
      showCloseButton: false,
      icon: false,
      component: <Toastr text={text} type="success" />,
    });
  },
  warning(text: string) {
    reduxToastr.warning("", " ", {
      showCloseButton: false,
      icon: false,
      component: <Toastr text={text} type="warning" />,
    });
  },
  error(text: string) {
    reduxToastr.error("", " ", {
      showCloseButton: false,
      icon: false,
      component: <Toastr text={text} type="error" />,
    });
  },
  info(text: string) {
    reduxToastr.info("", " ", {
      showCloseButton: false,
      icon: false,
      component: <Toastr text={text} type="info" />,
    });
  },
};



// WEBPACK FOOTER //
// ./src/utilities.js